import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AppointmentModal = ({ 
  appointment, 
  isOpen, 
  onClose, 
  onSave, 
  providers, 
  appointmentTypes,
  mode = 'view'
}) => {
  const [formData, setFormData] = useState(appointment || {
    patientName: '',
    patientMRN: '',
    providerId: '',
    appointmentType: '',
    date: '',
    time: '',
    duration: '30',
    notes: '',
    sendReminder: true,
    insuranceVerified: false
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  const isViewMode = mode === 'view' && appointment;
  const isEditMode = mode === 'edit' || mode === 'create';

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-background p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
              <Icon name="Calendar" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {isViewMode ? 'Appointment Details' : mode === 'edit' ? 'Edit Appointment' : 'New Appointment'}
              </h2>
              {isViewMode && (
                <p className="text-sm text-muted-foreground mt-1">
                  {new Date(appointment.startTime)?.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground transition-smooth hover:bg-muted hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {isViewMode ? (
            <>
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <Image
                  src={appointment?.patientAvatar}
                  alt={appointment?.patientAvatarAlt}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground">{appointment?.patientName}</h3>
                  <p className="text-sm text-muted-foreground">MRN: {appointment?.patientMRN}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      appointment?.status === 'confirmed' ? 'bg-success/10 text-success' :
                      appointment?.status === 'pending' ? 'bg-warning/10 text-warning' :
                      appointment?.status === 'cancelled' ? 'bg-muted text-muted-foreground' :
                      'bg-primary/10 text-primary'
                    }`}>
                      {appointment?.status?.charAt(0)?.toUpperCase() + appointment?.status?.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Provider</p>
                  <p className="text-base text-foreground">{appointment?.providerName}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Appointment Type</p>
                  <p className="text-base text-foreground">{appointment?.type}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Time</p>
                  <p className="text-base text-foreground">
                    {new Date(appointment.startTime)?.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Duration</p>
                  <p className="text-base text-foreground">{appointment?.duration} minutes</p>
                </div>
              </div>

              {appointment?.notes && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Notes</p>
                  <p className="text-base text-foreground">{appointment?.notes}</p>
                </div>
              )}

              {appointment?.insuranceStatus && (
                <div className="flex items-center gap-2 p-4 bg-success/10 rounded-lg">
                  <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  <span className="text-sm font-medium text-success">Insurance Verified</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Patient Name"
                  type="text"
                  placeholder="Enter patient name"
                  value={formData?.patientName}
                  onChange={(e) => handleChange('patientName', e?.target?.value)}
                  required
                />
                <Input
                  label="Patient MRN"
                  type="text"
                  placeholder="Enter MRN"
                  value={formData?.patientMRN}
                  onChange={(e) => handleChange('patientMRN', e?.target?.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Provider"
                  placeholder="Select provider"
                  options={providers}
                  value={formData?.providerId}
                  onChange={(value) => handleChange('providerId', value)}
                  required
                  searchable
                />
                <Select
                  label="Appointment Type"
                  placeholder="Select type"
                  options={appointmentTypes}
                  value={formData?.appointmentType}
                  onChange={(value) => handleChange('appointmentType', value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Date"
                  type="date"
                  value={formData?.date}
                  onChange={(e) => handleChange('date', e?.target?.value)}
                  required
                />
                <Input
                  label="Time"
                  type="time"
                  value={formData?.time}
                  onChange={(e) => handleChange('time', e?.target?.value)}
                  required
                />
                <Select
                  label="Duration"
                  options={[
                    { value: '15', label: '15 minutes' },
                    { value: '30', label: '30 minutes' },
                    { value: '45', label: '45 minutes' },
                    { value: '60', label: '1 hour' },
                    { value: '90', label: '1.5 hours' },
                    { value: '120', label: '2 hours' }
                  ]}
                  value={formData?.duration}
                  onChange={(value) => handleChange('duration', value)}
                  required
                />
              </div>

              <Input
                label="Notes"
                type="text"
                placeholder="Add appointment notes"
                value={formData?.notes}
                onChange={(e) => handleChange('notes', e?.target?.value)}
              />

              <div className="space-y-3">
                <Checkbox
                  label="Send appointment reminder"
                  description="Patient will receive SMS and email reminders"
                  checked={formData?.sendReminder}
                  onChange={(e) => handleChange('sendReminder', e?.target?.checked)}
                />
                <Checkbox
                  label="Insurance verified"
                  checked={formData?.insuranceVerified}
                  onChange={(e) => handleChange('insuranceVerified', e?.target?.checked)}
                />
              </div>
            </>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            {isViewMode ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={onClose}
                >
                  Close
                </Button>
                <Button
                  type="button"
                  variant="default"
                  fullWidth
                  iconName="Edit"
                  iconPosition="left"
                  onClick={() => {}}
                >
                  Edit Appointment
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  iconName="Save"
                  iconPosition="left"
                >
                  {mode === 'edit' ? 'Update' : 'Create'} Appointment
                </Button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentModal;